import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';
import {
  ITEM_STATUSES_V2,
  type ItemCondition,
  type ItemDeliveryLeg,
  type ItemStatus,
  type ItemStatusV2,
} from './status';

/**
 * A "package" here is a logistic **item** — same entity, client-facing name.
 * Status vocabulary lives in `./status`.
 */
export type PackageStatus = ItemStatus;

/**
 * Values the status dropdown may send: the whole v2 item vocabulary, including
 * `DISPATCHED` — which is filterable here even though `/v1/inbound/items`
 * rejects it (09 §3: two screens, two scopes). The BE 400s only on the
 * pre-migration values `DELIVERED` / `RETURNED` / `DAMAGED`, which rows written
 * before the data migration can still come back carrying.
 */
export const PACKAGE_STATUS_FILTERS = ITEM_STATUSES_V2;
export type PackageStatusFilter = ItemStatusV2;

/**
 * One proof-of-delivery photo for a package (POD-per-package), ingested by
 * logistic from the delivery-service `delivery-status-updated` event.
 * `mediaType` groups photos by kind on the detail drawer. Empty for non-POD
 * clients.
 */
export interface PackageMedia {
  id: string;
  mediaType: string;
  url: string;
  latitude?: string;
  longitude?: string;
  capturedAt?: string | null;
}

/**
 * One item status transition, from the logistic `item_status_history` audit
 * log. Drives the package-detail lifecycle timeline. Oldest-first.
 */
/**
 * Rider who performed a transition (e.g. the handover scan), resolved by the
 * backend from the batch snapshot. `id` matches the back-office driver page id.
 * Absent for system / ops-email transitions.
 */
export interface PackageTimelineRider {
  id: number;
  /** Rider code, e.g. `MTR000`. Null if the snapshot lacked one. */
  code?: string | null;
  /** Rider display name. Null if the snapshot lacked one. */
  name?: string | null;
}

export interface PackageTimelineEvent {
  id: string;
  fromStatus: string;
  toStatus: string;
  /** Actor email/identifier, or null (system-driven transition). */
  changedBy?: string | null;
  /** Rider identity when `changedBy` is a rider id (handover). Null otherwise. */
  rider?: PackageTimelineRider | null;
  /** Free-text note captured with the transition, or null. */
  note?: string | null;
  /** ISO timestamp of the transition. */
  changedAt: string;
}

export interface PackageRow {
  id: string;
  po: string;
  deliveryId: string;
  bookingId?: string;
  outlet: string;
  sender: string;
  // Owning client (company) — shown after the DASH wordmark on the printed label
  // header ("DASH | {client.name}"). Optional; the header omits it when absent.
  client?: { id: number; name: string };
  qty: number;
  weight: number;
  volume?: number;
  /**
   * Physical parcel count (dus) for this item — distinct from `qty` (units of
   * goods). Always present: the column defaults to 1, so the list projection
   * never returns it empty.
   */
  koli: number;
  status: PackageStatus;
  /**
   * Physical condition — the v2 attribute that replaced status `DAMAGED`, so a
   * damaged parcel keeps its real custody status and the flag survives.
   */
  condition?: ItemCondition | null;
  /** Retur details, present once the parcel has been refused by the recipient. */
  returReasonCode?: string | null;
  returReasonNote?: string | null;
  returWeight?: number | null;
  /** Every trip this parcel took — one `LAST_MILE` row per delivery attempt. */
  legs?: ItemDeliveryLeg[];
  activeLeg?: ItemDeliveryLeg | null;
  recipientAddress?: string;
  senderAddress?: string;
  itemName?: string;
  // Position of this item within its shipment (waybill) and the shipment's
  // total item count — drives the "X / Y" badge on the printed label.
  itemSeq?: number;
  itemTotal?: number;
  createdAt?: string;
  media?: PackageMedia[];
  /** Status-transition history, oldest-first. Empty when none recorded. */
  timeline?: PackageTimelineEvent[];
}

export interface PackageListParams {
  // Optional: logistic ops (ADMIN) lists across all clients when omitted.
  clientId?: number;
  search?: string;
  /** v2 values only — see `PACKAGE_STATUS_FILTERS`. */
  status?: PackageStatusFilter;
  /** Damaged-parcel filter, orthogonal to `status`. */
  condition?: ItemCondition;
  recipient?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

/**
 * `GET /v1/packages/summary` — per-status counts, v2 keys (09 §2).
 *
 * ⚠️ **Name trap.** `verifiedByHub` / `verifiedByRider` count status
 * `HUB_SCANNED` / `HANDOVER_SCANNED`. The key names are leftovers from a rename
 * that was withdrawn — there is no `VERIFIED_BY_*` status. They were kept
 * because FE had already bound to them.
 *
 * `dispatched` **is** a bucket here (unlike `/v1/inbound/items/counts`), and
 * `total` is the sum of every field — drop one from a card row and the numbers
 * stop reconciling. All optional so a BE that omits an empty bucket, or an
 * older build, degrades to a blank chip; `hubScanned` / `handoverScanned` stay
 * as read-fallbacks in case a build emits the plain spellings.
 */
export interface PackageSummary {
  total?: number;
  created?: number;
  /** Status `HUB_SCANNED` — see the name trap above. */
  verifiedByHub?: number;
  dispatched?: number;
  /** Status `HANDOVER_SCANNED` — see the name trap above. */
  verifiedByRider?: number;
  inDelivery?: number;
  completed?: number;
  inReturn?: number;
  returnedToHub?: number;
  returnedToClient?: number;
  cancelled?: number;
  lost?: number;
  /** Fallback spelling for `verifiedByHub`. */
  hubScanned?: number;
  /** Fallback spelling for `verifiedByRider`. */
  handoverScanned?: number;
}

export type LabelLayout = 'THERMAL_4X6' | 'THERMAL_100X100' | 'A4_2UP';

export interface PackageListResult {
  packages: PackageRow[];
  total: number;
  page: number;
  limit: number;
}

export const getPackages = async (
  params: PackageListParams
): Promise<PackageListResult> => {
  try {
    const res = await Axios.get('/v1/packages', { params });
    return res.data.data as PackageListResult;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/** Per-status counts under the same filters as `getPackages` (minus paging). */
export const getPackageSummary = async (
  params: Omit<PackageListParams, 'page' | 'limit' | 'status'> = {}
): Promise<PackageSummary> => {
  try {
    const res = await Axios.get('/v1/packages/summary', { params });
    return (res.data.data as PackageSummary) ?? {};
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * Soft-cancel a single package (item). The backend flips its status to
 * CANCELLED and recomputes the parent shipment's totals. Only CREATED items are
 * cancellable; the server rejects others.
 */
export const cancelPackage = async (
  id: string,
  reason?: string
): Promise<void> => {
  try {
    await Axios.post(`/v1/packages/${id}/cancel`, {
      reason: reason || undefined,
    });
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * Edit a package's weight (kg, > 0), volume (m³, ≥ 0) and koli (whole parcel
 * count, 1–10000). All three are required by the BE DTO — the edit form always
 * submits the full set.
 */
export const updatePackage = async (
  id: string,
  body: { weight: number; volume: number; koli: number }
): Promise<void> => {
  try {
    await Axios.patch(`/v1/packages/${id}`, body);
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const packagesAPI = {
  list: getPackages,
  summary: getPackageSummary,
  cancel: cancelPackage,
  update: updatePackage,
};
